import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowRight,
  Wind,
  Snowflake,
  Leaf,
  ShieldCheck,
  Clock,
  Award,
  Phone,
  Mail,
  MapPin,
  Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// UI constants
const HERO_STRIP_BASE_DELAY_MS = 420;
const HERO_STRIP_STEP_DELAY_MS = 90;
const ICON_LG_SIZE = 22;
const TOAST_RESET_MS = 4000;

// Tailwind col-span per service card position
const SERVICE_COL_SPAN = ["md:col-span-7", "md:col-span-5", "md:col-span-12"];

const getServiceColSpan = (index) =>
  SERVICE_COL_SPAN[index] ?? "md:col-span-12";

const getSubmitLabel = ({ sent, loading }) => {
  if (sent) return "Skickat ✓";
  if (loading) return "Skickar…";
  return "Skicka förfrågan";
};

const SERVICES = [
  {
    id: "ventilation",
    num: "01",
    icon: Wind,
    title: "Ventilation",
    kicker: "Rent inomhusklimat",
    desc:
      "Vi rengör befintliga ventilationssystem och installerar nya, moderna lösningar för bostäder, BRF:er och kommersiella fastigheter.",
    img:
      "https://images.unsplash.com/photo-1708005383082-3a0e5e79a347?auto=format&fit=crop&w=1600&q=80",
    bullets: ["OVK-besiktning", "Kanalrengöring", "Injustering & montage"],
  },
  {
    id: "vintertjanster",
    num: "02",
    icon: Snowflake,
    title: "Vintertjänster",
    kicker: "Säker snöröjning",
    desc:
      "Effektiv snöröjning med traktor och säker takskottning utförd av erfarna team – hela vintersäsongen.",
    img:
      "https://images.unsplash.com/photo-1732645556381-161e81915288?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    bullets: ["Snöröjning med traktor", "Takskottning", "Halkbekämpning"],
  },
  {
    id: "tradgard",
    num: "03",
    icon: Leaf,
    title: "Trädgård & Innergård",
    kicker: "Välvårdat året om",
    desc:
      "Gräsklippning, rengöring, plantering och löpande skötsel – vi tar hand om helheten så att gården alltid ser välvårdad ut.",
    img:
      "https://images.unsplash.com/photo-1762461838534-ca26dfa134a8?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    bullets: ["Gräsklippning & häck", "Plantering", "Löpande skötsel"],
  },
];

const PROCESS = [
  { n: "01", t: "Förfrågan", d: "Skicka en offertförfrågan – svar inom 24 timmar på vardagar." },
  { n: "02", t: "Bedömning", d: "Vi tittar på plats eller via samtal och presenterar en tydlig offert." },
  { n: "03", t: "Leverans", d: "Vårt team utför jobbet med god kommunikation genom hela processen." },
  { n: "04", t: "Uppföljning", d: "Vi följer upp för att säkerställa att du är nöjd med resultatet." },
];

const MARQUEE = [
  "Ventilation",
  "OVK-besiktning",
  "Kanalrengöring",
  "Snöröjning",
  "Takskottning",
  "Halkbekämpning",
  "Trädgårdsskötsel",
  "Gräsklippning",
  "Plantering",
  "Allt fix",
  "Projektering",
  "Montage",
];

function Header() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#f4efe6cc] border-b border-[#14141314]" data-testid="site-header">
      <div className="max-w-[1360px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="logo-link">
          <span className="w-8 h-8 rounded-full bg-[#141413] text-[#f4efe6] grid place-items-center font-display text-[16px]">B</span>
          <span className="font-display text-[18px] tracking-tight">Big Space<span className="text-[var(--accent)]">.</span></span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-[14px] text-[#39352d]">
          <a href="#tjanster" className="link-underline" data-testid="nav-tjanster">Tjänster</a>
          <a href="#arbetssatt" className="link-underline" data-testid="nav-arbetssatt">Så jobbar vi</a>
          <a href="#om" className="link-underline" data-testid="nav-om">Om oss</a>
          <a href="#offert" className="link-underline" data-testid="nav-kontakt">Kontakt</a>
        </nav>
        <a href="#offert" className="btn-primary" data-testid="header-cta-offert">
          Begär offert <ArrowUpRight size={16} />
        </a>
      </div>
    </header>
  );
}

function HeroIntro() {
  return (
    <div className="max-w-[1360px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10 md:pb-16">
      <div className="eyebrow mb-8 rise">Entreprenad · Montage · Projektering</div>
      <h1 className="font-display font-light text-[44px] leading-[1.02] md:text-[112px] md:leading-[0.96] tracking-tight max-w-[12ch] rise delay-1">
        Fastighetsservice<br />
        med <em className="italic text-[var(--accent)] not-italic-alt">förtroende.</em>
      </h1>
      <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8 items-end">
        <p className="md:col-span-6 text-[17px] md:text-[19px] leading-relaxed text-[#39352d] max-w-[52ch] rise delay-2">
          Ventilation, snöröjning och trädgårdsskötsel — utfört av ett svenskt team
          som levererar kvalitet från projektering till färdigt resultat.
        </p>
        <div className="md:col-span-6 flex flex-wrap gap-3 rise delay-3 md:justify-end">
          <a href="#offert" className="btn-primary" data-testid="hero-cta-offert">
            Begär offert <ArrowRight size={16} />
          </a>
          <a href="#tjanster" className="btn-ghost" data-testid="hero-cta-tjanster">
            Våra tjänster
          </a>
        </div>
      </div>
      <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-5 rise delay-4">
        <TrustBadge icon={ShieldCheck} title="Försäkrade & trygga" sub="F-skatt, ansvarsförsäkring & referenser" />
        <TrustBadge icon={Award} title="Över 20 års erfarenhet" sub="Beprövat hantverk genom tre decennier" highlight />
        <TrustBadge icon={Clock} title="Snabbt svar på förfrågan" sub="Återkoppling inom 24h på vardagar" />
      </div>
    </div>
  );
}

function HeroStripCard({ service, index }) {
  return (
    <a
      href={`#${service.id}`}
      className="group relative overflow-hidden rounded-[22px] aspect-[4/3] grain rise"
      style={{ animationDelay: `${HERO_STRIP_BASE_DELAY_MS + index * HERO_STRIP_STEP_DELAY_MS}ms` }}
      data-testid={`hero-strip-${service.id}`}
    >
      <img src={service.img} alt={service.title} className="card-img absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#141413cc] via-[#14141333] to-transparent" />
      <div className="relative z-10 h-full p-5 md:p-6 flex flex-col justify-between text-[#f4efe6]">
        <span className="num-tag opacity-80">{service.num} · {service.title}</span>
        <div className="flex items-end justify-between">
          <span className="font-display text-[22px] md:text-[26px] leading-none">{service.kicker}</span>
          <ArrowUpRight size={ICON_LG_SIZE} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </div>
      </div>
    </a>
  );
}

function HeroImageStrip() {
  return (
    <div className="relative max-w-[1360px] mx-auto px-6 md:px-10 pb-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
        {SERVICES.map((s, i) => <HeroStripCard key={s.id} service={s} index={i} />)}
      </div>
    </div>
  );
}

function Marquee() {
  return (
    <div className="relative mt-10 md:mt-16 border-y border-[#14141314] overflow-hidden py-5 bg-[var(--bg-2)]">
      <div className="flex whitespace-nowrap marquee-track">
        {[...MARQUEE, ...MARQUEE].map((w, i) => (
          <span key={`${w}-${i < MARQUEE.length ? "a" : "b"}`} className="mx-8 font-display text-[26px] md:text-[36px] text-[#14141399] italic">
            {w} <span className="text-[var(--accent)] not-italic">/</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      <HeroIntro />
      <HeroImageStrip />
      <Marquee />
    </section>
  );
}

function TrustBadge({ icon: Icon, title, sub, highlight }) {
  return (
    <div
      className={`flex items-start gap-4 p-5 rounded-2xl border ${
        highlight ? "bg-[var(--ink)] text-[var(--bg)] border-transparent" : "border-[#14141314] bg-[#ffffff55]"
      }`}
      data-testid={`trust-${title.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <span
        className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
          highlight ? "bg-[var(--accent)] text-white" : "bg-[var(--ink)] text-[var(--bg)]"
        }`}
      >
        <Icon size={18} />
      </span>
      <div>
        <div className={`font-display text-[18px] leading-tight ${highlight ? "" : ""}`}>{title}</div>
        <div className={`text-[13.5px] mt-1 ${highlight ? "text-[#f4efe6aa]" : "text-[#6e6a60]"}`}>{sub}</div>
      </div>
    </div>
  );
}

function ServiceCard({ service, index }) {
  const isWide = index === 2;
  return (
    <article
      id={service.id}
      className={`card-hover group relative overflow-hidden rounded-[28px] border border-[#14141314] bg-white ${getServiceColSpan(index)}`}
      data-testid={`service-card-${service.id}`}
    >
      <div className={`grid ${isWide ? "md:grid-cols-2" : "grid-cols-1"}`}>
        <div className={`relative ${isWide ? "aspect-[16/10] md:aspect-auto md:min-h-[460px]" : "aspect-[16/10]"} overflow-hidden`}>
          <img src={service.img} alt={service.title} className="card-img absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f4efe6e6] text-[#141413] num-tag">
            {service.num} · {service.title}
          </div>
        </div>
        <div className="p-7 md:p-10 flex flex-col gap-5">
          <div className="flex items-center gap-3 text-[var(--accent)]">
            <service.icon size={ICON_LG_SIZE} />
            <span className="eyebrow text-[var(--accent)]">{service.kicker}</span>
          </div>
          <h3 className="font-display text-[30px] md:text-[40px] leading-[1.02]">{service.title}</h3>
          <p className="text-[15.5px] md:text-[16.5px] leading-relaxed text-[#39352d] max-w-[48ch]">{service.desc}</p>
          <ul className="mt-1 space-y-2">
            {service.bullets.map((b) => (
              <li key={b} className="flex items-center gap-2.5 text-[14.5px] text-[#39352d]">
                <Check size={16} className="text-[var(--accent)]" /> {b}
              </li>
            ))}
          </ul>
          <a href="#offert" className="mt-3 inline-flex items-center gap-2 font-semibold text-[14.5px] link-underline w-fit" data-testid={`service-cta-${service.id}`}>
            Begär offert <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </article>
  );
}

function AlltFixBand() {
  return (
    <div className="mt-10 md:mt-14 relative rounded-[28px] overflow-hidden bg-[var(--ink)] text-[var(--bg)] grain">
      <div className="relative z-10 grid md:grid-cols-12 items-center gap-8 p-8 md:p-14">
        <div className="md:col-span-7">
          <div className="eyebrow text-[#f4efe699] mb-4">Allt fix</div>
          <h3 className="font-display text-[30px] md:text-[52px] leading-[1.02] max-w-[22ch]">
            Något speciellt som behöver lösas? <em className="italic text-[var(--accent)]">Vi tittar på det.</em>
          </h3>
          <p className="mt-5 text-[15.5px] md:text-[17px] text-[#f4efe6cc] max-w-[60ch]">
            Vi tar oss gärna an projekt utanför våra kärnområden. Hör av dig så tittar vi på det tillsammans —
            en kontaktperson, ett uppdrag, hela vägen till färdigt resultat.
          </p>
        </div>
        <div className="md:col-span-5 flex md:justify-end">
          <a href="#offert" className="btn-primary" style={{ background: "var(--accent)", borderColor: "var(--accent)", color: "#fff" }} data-testid="alltfix-cta">
            Beskriv ditt projekt <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ServicesHeader() {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
      <div>
        <div className="eyebrow mb-5">Våra tjänster</div>
        <h2 className="font-display font-light text-[40px] md:text-[72px] leading-[1] tracking-tight max-w-[16ch]">
          Tre kärnområden. <em className="italic text-[var(--accent)]">Obegränsat</em> engagemang.
        </h2>
      </div>
      <p className="text-[16px] md:text-[17px] text-[#39352d] max-w-[44ch]">
        Vi är specialister på ventilation, vintertjänster och trädgårdsskötsel — men vi tar också
        hand om ”allt fix” däremellan. En partner, hela året.
      </p>
    </div>
  );
}

function Services() {
  return (
    <section id="tjanster" className="relative py-24 md:py-36" data-testid="services-section">
      <div className="max-w-[1360px] mx-auto px-6 md:px-10">
        <ServicesHeader />
        <div className="grid md:grid-cols-12 gap-5 md:gap-6">
          {SERVICES.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
        </div>
        <AlltFixBand />
      </div>
    </section>
  );
}

function Process() {
  return (
    <section id="arbetssatt" className="dark-section py-24 md:py-36 relative overflow-hidden" data-testid="process-section">
      <div className="max-w-[1360px] mx-auto px-6 md:px-10 relative">
        <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow text-[#f4efe699] mb-5">Så jobbar vi</div>
            <h2 className="font-display font-light text-[40px] md:text-[80px] leading-[0.98] tracking-tight max-w-[14ch]">
              Enkelt, tryggt <em className="italic text-[var(--accent)]">och på riktigt.</em>
            </h2>
          </div>
          <p className="md:col-span-5 text-[16px] md:text-[17px] text-[#f4efe6bb] max-w-[44ch]">
            Du skickar en förfrågan, vi återkommer med offert, och sen levererar vi. Inga dolda kostnader.
            Inga onödiga mellanhänder.
          </p>
        </div>

        <ol className="grid md:grid-cols-4 gap-5">
          {PROCESS.map((p, i) => (
            <li
              key={p.n}
              className="relative p-7 rounded-[22px] bg-[#ffffff0a] border border-[#ffffff14] hover:bg-[#ffffff12] transition-colors"
              data-testid={`process-step-${p.n}`}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-display text-[48px] leading-none text-[var(--accent)]">{p.n}</span>
                {i < PROCESS.length - 1 && <ArrowRight size={18} className="text-[#f4efe666]" />}
              </div>
              <h4 className="font-display text-[24px] mb-2">{p.t}</h4>
              <p className="text-[14.5px] leading-relaxed text-[#f4efe6aa]">{p.d}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function AboutStrip() {
  return (
    <section id="om" className="py-24 md:py-32" data-testid="about-section">
      <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <div className="eyebrow mb-5">Om Big Space</div>
          <h2 className="font-display font-light text-[38px] md:text-[64px] leading-[1] tracking-tight">
            Ett svenskt team med <em className="italic text-[var(--accent)]">hantverk</em> i blodet.
          </h2>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
          <Stat k="20+" v="års erfarenhet inom fastighetsservice" />
          <Stat k="1" v="kontaktperson – hela projektet igenom" />
          <Stat k="24h" v="svarstid på offertförfrågan" />
          <Stat k="100%" v="försäkrade och dokumenterade uppdrag" />
        </div>
      </div>
    </section>
  );
}

function Stat({ k, v }) {
  return (
    <div className="p-7 rounded-[22px] bg-white border border-[#14141314]">
      <div className="font-display text-[56px] md:text-[64px] leading-none text-[var(--ink)]">{k}</div>
      <div className="mt-3 text-[14.5px] text-[#39352d] max-w-[28ch]">{v}</div>
    </div>
  );
}

const INITIAL_FORM = { name: "", email: "", phone: "", service: "Ventilation", message: "" };

function useQuoteSubmit() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Fyll i namn, e-post och beskrivning.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/offert`, form);
      setSent(true);
      toast.success("Tack! Vi återkommer inom 24 timmar på vardagar.");
      setForm(INITIAL_FORM);
      setTimeout(() => setSent(false), TOAST_RESET_MS);
    } catch {
      toast.error("Något gick fel. Maila oss på jobb@bigspace.se så löser vi det.");
    } finally {
      setLoading(false);
    }
  };

  return { form, update, submit, loading, sent };
}

function QuoteSidebar() {
  return (
    <div className="md:col-span-5">
      <div className="eyebrow mb-5">Redo att starta?</div>
      <h2 className="font-display font-light text-[40px] md:text-[68px] leading-[1] tracking-tight">
        Låt oss ta hand om det — <em className="italic text-[var(--accent)]">från start till mål.</em>
      </h2>
      <p className="mt-6 text-[16px] text-[#39352d] max-w-[48ch]">
        Skicka en offertförfrågan så återkommer vi inom 24 timmar på vardagar med en tydlig plan och pris.
      </p>
      <div className="mt-10 space-y-4">
        <a href="mailto:jobb@bigspace.se" className="flex items-center gap-3 text-[15px] link-underline w-fit" data-testid="contact-email">
          <Mail size={18} className="text-[var(--accent)]" /> jobb@bigspace.se
        </a>
        <div className="flex items-center gap-3 text-[15px]">
          <MapPin size={18} className="text-[var(--accent)]" /> Verksamma i hela Sverige
        </div>
        <div className="flex items-center gap-3 text-[15px]">
          <Clock size={18} className="text-[var(--accent)]" /> Mån–Fre · 07:00–17:00
        </div>
      </div>
    </div>
  );
}

function QuoteFormFields({ form, update }) {
  return (
    <>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="field">
          <label htmlFor="name">Namn *</label>
          <input id="name" data-testid="quote-input-name" value={form.name} onChange={update("name")} placeholder="För- och efternamn" required />
        </div>
        <div className="field">
          <label htmlFor="email">E-post *</label>
          <input id="email" type="email" data-testid="quote-input-email" value={form.email} onChange={update("email")} placeholder="din@epost.se" required />
        </div>
        <div className="field">
          <label htmlFor="phone">Telefon</label>
          <input id="phone" data-testid="quote-input-phone" value={form.phone} onChange={update("phone")} placeholder="070-123 45 67" />
        </div>
        <div className="field">
          <label htmlFor="service">Tjänst</label>
          <select id="service" data-testid="quote-input-service" value={form.service} onChange={update("service")}>
            <option>Ventilation</option>
            <option>Vintertjänster</option>
            <option>Trädgård & Innergård</option>
            <option>Allt fix / annat</option>
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="message">Beskriv projektet *</label>
        <textarea id="message" data-testid="quote-input-message" value={form.message} onChange={update("message")} placeholder="Kort om adress, omfattning och tidsram…" required />
      </div>
    </>
  );
}

function QuoteForm() {
  const { form, update, submit, loading, sent } = useQuoteSubmit();
  return (
    <section id="offert" className="py-24 md:py-36 bg-[var(--bg-2)]" data-testid="quote-section">
      <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <QuoteSidebar />
        <form onSubmit={submit} className="md:col-span-7 bg-white rounded-[28px] border border-[#14141314] p-7 md:p-10 grid gap-6" data-testid="quote-form">
          <QuoteFormFields form={form} update={update} />
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-[13px] text-[#6e6a60] max-w-[50ch]">
              Genom att skicka godkänner du att vi lagrar dina uppgifter för att återkoppla på din förfrågan.
            </p>
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="quote-submit-button">
              {getSubmitLabel({ sent, loading })}
              {!loading && !sent && <ArrowRight size={16} />}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="dark-section pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-[1360px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 pb-16 border-b border-[#ffffff1f]">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-9 h-9 rounded-full bg-[var(--bg)] text-[#141413] grid place-items-center font-display text-[16px]">B</span>
              <span className="font-display text-[22px]">Big Space AB<span className="text-[var(--accent)]">.</span></span>
            </div>
            <p className="text-[15px] text-[#f4efe6bb] max-w-[44ch]">
              Ventilation, snöröjning och fastighetsservice. Entreprenad, montage och projektering i Sverige.
            </p>
          </div>
          <div className="md:col-span-3">
            <div className="eyebrow text-[#f4efe699] mb-5">Tjänster</div>
            <ul className="space-y-3 text-[15px]">
              <li><a href="#ventilation" className="link-underline">Ventilation</a></li>
              <li><a href="#vintertjanster" className="link-underline">Vintertjänster</a></li>
              <li><a href="#tradgard" className="link-underline">Trädgård & Innergård</a></li>
              <li><a href="#offert" className="link-underline">Allt fix</a></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="eyebrow text-[#f4efe699] mb-5">Kontakt</div>
            <ul className="space-y-3 text-[15px]">
              <li className="flex items-center gap-3"><Mail size={16} className="text-[var(--accent)]" /> <a href="mailto:jobb@bigspace.se" className="link-underline">jobb@bigspace.se</a></li>
              <li className="flex items-center gap-3"><Phone size={16} className="text-[var(--accent)]" /> <a href="tel:+46" className="link-underline">Kontakta via e-post</a></li>
              <li className="flex items-center gap-3"><MapPin size={16} className="text-[var(--accent)]" /> Verksamma i hela Sverige</li>
            </ul>
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[13px] text-[#f4efe699]">
          <div>© {new Date().getFullYear()} Big Space AB. Alla rättigheter förbehållna.</div>
          <div className="flex items-center gap-6">
            <span>Försäkrade & F-skatt</span>
            <span>Över 20 års erfarenhet</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function BigSpace() {
  useEffect(() => {
    // warm up backend on mount
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen" data-testid="bigspace-app">
      <Header />
      <Hero />
      <Services />
      <Process />
      <AboutStrip />
      <QuoteForm />
      <Footer />
    </main>
  );
}
